import { Button } from 'shared/components/ui';
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color, BackgroundColor, FontSize } from '@tiptap/extension-text-style';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import Link from '@tiptap/extension-link';
import {
  FormatBold, FormatItalic, FormatUnderlined, StrikethroughS,
  FormatListBulleted, FormatListNumbered, FormatQuote, FormatClear,
  Undo, Redo, Link as LinkIcon, ImageOutlined, OndemandVideo,
  FormatColorText, FormatColorFill, Close,
} from '@mui/icons-material';

const TAMANOS_FUENTE = ['12px', '14px', '16px', '18px', '24px', '32px', '48px'];

const ToolButton = ({ label, icon: Icon, active, disabled, onClick, children }) => (
  <button type="button" className={'cms-tool-button' + (active ? ' is-active' : '')}
    title={label} aria-label={label} aria-pressed={active} disabled={disabled}
    onMouseDown={(event) => event.preventDefault()} onClick={onClick}>
    <Icon fontSize="small" />
    {children && <span>{children}</span>}
  </button>
);

const CmsEditor = ({ value = '', onChange, onUploadMedia, allowLink = true }) => {
  const fileInputRef = useRef(null);
  const [insert, setInsert] = useState(null);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      TextStyle, Color, BackgroundColor, FontSize, Image,
      Youtube.configure({ controls: true, nocookie: true }),
      ...(allowLink ? [Link.configure({ openOnClick: false })] : []),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Editor de contenido',
        'aria-describedby': 'cms-editor-help',
        'data-placeholder': 'Escribí el contenido de la publicación…',
        class: 'cms-editor-area',
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML(), { length: ed.getText().length, empty: ed.isEmpty }),
  });

  // Subscribe to transactions so selection changes update the toolbar in TipTap 3.
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => ed ? {
      bold: ed.isActive('bold'), italic: ed.isActive('italic'),
      underline: ed.isActive('underline'), strike: ed.isActive('strike'),
      bulletList: ed.isActive('bulletList'), orderedList: ed.isActive('orderedList'),
      blockquote: ed.isActive('blockquote'), link: ed.isActive('link'),
      block: ed.isActive('heading', { level: 1 }) ? '1' : ed.isActive('heading', { level: 2 }) ? '2' : ed.isActive('heading', { level: 3 }) ? '3' : '',
      fontSize: ed.getAttributes('textStyle').fontSize || '',
      color: ed.getAttributes('textStyle').color || '#243247',
      backgroundColor: ed.getAttributes('textStyle').backgroundColor || '#fff3a3',
      canUndo: ed.can().undo(), canRedo: ed.can().redo(),
      characters: ed.getText().length, empty: ed.isEmpty,
    } : null,
  });

  useEffect(() => {
    if (editor && (value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
      onChange?.(editor.getHTML(), { length: editor.getText().length, empty: editor.isEmpty });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor || !state) return null;

  const openInsert = (kind) => {
    setInsert(insert === kind ? null : kind);
    setUrl(kind === 'link' ? editor.getAttributes('link').href || '' : '');
    setError('');
  };

  const applyUrl = () => {
    try {
      const parsed = new URL(url.trim());
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
      if (insert === 'video') {
        if (!['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'www.youtube-nocookie.com'].includes(parsed.hostname)) throw new Error();
        if (!editor.chain().focus().setYoutubeVideo({ src: parsed.href }).run()) throw new Error();
      } else {
        const chain = editor.chain().focus().extendMarkRange('link');
        if (editor.state.selection.empty && !state.link) {
          chain.insertContent({ type: 'text', text: parsed.href, marks: [{ type: 'link', attrs: { href: parsed.href } }] }).run();
        } else {
          chain.setLink({ href: parsed.href }).run();
        }
      }
      setInsert(null);
      setError('');
    } catch {
      setError(insert === 'video' ? 'Ingresá una URL válida de YouTube.' : 'Ingresá una URL completa que empiece con https:// o http://.');
    }
  };

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !onUploadMedia) return;
    setUploading(true);
    setError('');
    try {
      const imageUrl = await onUploadMedia(file);
      if (imageUrl && !editor.isDestroyed) editor.chain().focus().setImage({ src: imageUrl }).run();
    } catch {
      setError('No se pudo subir la imagen. Intentá nuevamente.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={'cms-editor' + (state.empty ? ' is-empty' : '')}>
      <div className="cms-editor-toolbar" role="toolbar" aria-label="Formato del contenido">
        <div className="cms-tool-group" role="group" aria-label="Estilo de texto">
          <select className="form-select form-select-sm cms-block-select" aria-label="Estilo de párrafo" value={state.block}
            onChange={(event) => event.target.value ? editor.chain().focus().setHeading({ level: Number(event.target.value) }).run() : editor.chain().focus().setParagraph().run()}>
            <option value="">Texto normal</option><option value="1">Título principal</option><option value="2">Subtítulo</option><option value="3">Título pequeño</option>
          </select>
          <select className="form-select form-select-sm cms-size-select" aria-label="Tamaño de fuente" value={state.fontSize}
            onChange={(event) => event.target.value ? editor.chain().focus().setFontSize(event.target.value).run() : editor.chain().focus().unsetFontSize().run()}>
            <option value="">Tamaño</option>
            {state.fontSize && !TAMANOS_FUENTE.includes(state.fontSize) && <option value={state.fontSize}>{state.fontSize}</option>}
            {TAMANOS_FUENTE.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </div>
        <div className="cms-tool-group" role="group" aria-label="Formato de texto">
          <ToolButton label="Negrita (Ctrl+B)" icon={FormatBold} active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()} />
          <ToolButton label="Cursiva (Ctrl+I)" icon={FormatItalic} active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()} />
          <ToolButton label="Subrayado (Ctrl+U)" icon={FormatUnderlined} active={state.underline} onClick={() => editor.chain().focus().toggleUnderline().run()} />
          <ToolButton label="Tachado" icon={StrikethroughS} active={state.strike} onClick={() => editor.chain().focus().toggleStrike().run()} />
        </div>
        <div className="cms-tool-group" role="group" aria-label="Colores">
          <label className="cms-color-tool" title="Color de texto">
            <FormatColorText fontSize="small" /><span className="cms-color-swatch" style={{ background: state.color }} />
            <input type="color" aria-label="Color de texto" value={/^#[0-9a-f]{6}$/i.test(state.color) ? state.color : '#243247'} onChange={(event) => editor.chain().focus().setColor(event.target.value).run()} />
          </label>
          <label className="cms-color-tool" title="Resaltar texto">
            <FormatColorFill fontSize="small" /><span className="cms-color-swatch" style={{ background: state.backgroundColor }} />
            <input type="color" aria-label="Resaltar texto" value={/^#[0-9a-f]{6}$/i.test(state.backgroundColor) ? state.backgroundColor : '#fff3a3'} onChange={(event) => editor.chain().focus().setBackgroundColor(event.target.value).run()} />
          </label>
          <ToolButton label="Quitar formato" icon={FormatClear} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} />
        </div>
        <div className="cms-tool-group cms-tool-history" role="group" aria-label="Historial">
          <ToolButton label="Deshacer (Ctrl+Z)" icon={Undo} disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()} />
          <ToolButton label="Rehacer (Ctrl+Shift+Z)" icon={Redo} disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()} />
        </div>
        <div className="cms-toolbar-bottom">
          <div className="cms-tool-group" role="group" aria-label="Listas y citas">
            <ToolButton label="Lista con viñetas" icon={FormatListBulleted} active={state.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()} />
            <ToolButton label="Lista numerada" icon={FormatListNumbered} active={state.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
            <ToolButton label="Cita" icon={FormatQuote} active={state.blockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
          </div>
          <div className="cms-tool-group cms-tool-insert" role="group" aria-label="Insertar contenido">
            {allowLink && <ToolButton label="Insertar o editar enlace" icon={LinkIcon} active={state.link || insert === 'link'} onClick={() => openInsert('link')}>Enlace</ToolButton>}
            <ToolButton label="Insertar imagen" icon={ImageOutlined} disabled={!onUploadMedia || uploading} onClick={() => fileInputRef.current?.click()}>{uploading ? 'Subiendo…' : 'Imagen'}</ToolButton>
            <ToolButton label="Insertar video de YouTube" icon={OndemandVideo} active={insert === 'video'} onClick={() => openInsert('video')}>Video</ToolButton>
          </div>
        </div>
      </div>
      {insert && (
        <div className="cms-insert-panel" role="group" aria-label={insert === 'video' ? 'Insertar video' : 'Editar enlace'}>
          <label htmlFor="cms-insert-url">{insert === 'video' ? 'URL del video de YouTube' : 'Dirección del enlace'}</label>
          <div className="cms-insert-controls">
            <input id="cms-insert-url" type="url" className="form-control form-control-sm" placeholder={insert === 'video' ? 'https://www.youtube.com/watch?v=…' : 'https://ejemplo.com'} value={url} autoFocus
              aria-invalid={!!error} aria-describedby={error ? 'cms-editor-error' : undefined} onChange={(event) => { setUrl(event.target.value); setError(''); }}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); applyUrl(); } if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setInsert(null); setError(''); editor.commands.focus(); } }} />
            <Button type="button" className="btn btn-sm btn-success" disabled={!url.trim()} onClick={applyUrl}>Aplicar</Button>
            {insert === 'link' && state.link && <Button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { editor.chain().focus().extendMarkRange('link').unsetLink().run(); setInsert(null); setError(''); }}>Quitar enlace</Button>}
            <ToolButton label="Cerrar opciones de inserción" icon={Close} onClick={() => { setInsert(null); setError(''); editor.commands.focus(); }} />
          </div>
        </div>
      )}
      {error && <p className="cms-editor-error" id="cms-editor-error" role="alert">{error}</p>}
      <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" hidden onChange={handleImage} />
      <EditorContent editor={editor} />
      <div className="cms-editor-status">
        <span id="cms-editor-help">Seleccioná texto para darle formato.</span>
        <span>{state.characters.toLocaleString('es-AR')} caracteres</span>
      </div>
    </div>
  );
};

export default CmsEditor;
